/**
 * Country-localised theft stats (AU + US). One source of truth so every
 * urgency/stats surface stays consistent. Pick a block with `theftStatsFor(isUS)`
 * from inside any component that already uses `useCountry()`.
 *
 * US sources: NICB (National Insurance Crime Bureau) 2025 report;
 * EngiMarket 2025 theft analysis (hand tools, heavy equipment, trailers).
 * AU sources: ABS Recorded Crime 2024–25; RACV; Queensland Police.
 */

const AU = {
  country: "Australia",
  flagLabel: "Australia · 2024–25",
  routeLabel: "Australian theft statistics",
  canonical: "/australian-theft-statistics",
  seoTitle: "Australian Theft Statistics 2025 | Tracksy",
  seoDescription:
    "The real numbers behind Australia's asset theft problem — vehicles, trailers and tools stolen every year, and why most are never recovered. Sources: ABS, RACV, Queensland Police.",

  // Headline (short) stats used on the home TheftUrgency panel.
  headline: "66,625 vehicles stolen last year.",
  subhead: "Fewer than 1 in 20 tools are recovered.",
  bodyCopy:
    "Australia's asset theft problem is quietly getting worse — cars, trailers, toolboxes, bikes, boats. Police can't be everywhere. By the time it's flagged stolen, the trail is cold. The best time to track what's yours was yesterday. The second best is right now.",
  bricks: [
    { big: "66,625", small: "Vehicle theft victims nationwide" },
    { big: "$40.8m", small: "Victorian tool theft, 1 year" },
    { big: "~5%", small: "Of stolen tools returned" },
  ],
  // The big donut on the home panel + the stats page.
  donut: { percent: 95, label: "Of stolen tools are NEVER recovered by police" },
  donutLabelShort: "Never recovered",
  donutCopy:
    "Industry research citing Queensland Police puts the return rate for stolen tools at roughly 5%. For every 20 tools taken, 19 stay gone.",
  donutSource: "MapTrack industry research · Queensland Police figures.",
  sourcesLabel: "Sources: Australian Bureau of Statistics · RACV · Queensland Police",

  // Full /theft-statistics page content.
  heroBadge: "Australian theft statistics · 2024–25",
  heroHeadline: "Someone's getting robbed",
  heroHeadlineAccent: "every 8 minutes.",
  heroBody:
    "Vehicles, trailers, tools, bikes, boats, jobsite kit. Australia is in the middle of a quiet theft wave — and once your gear is gone, the stats say it almost never comes back. These are the real, source-backed numbers. The authorities won't find most of it. Time to protect yourself.",
  cards: [
    {
      iconKey: "car",
      big: "66,625",
      label: "Motor vehicle theft victims recorded in Australia",
      sub: "Up 2% year-on-year.",
      source: "ABS Recorded Crime — Victims, 2025",
    },
    {
      iconKey: "hammer",
      big: "$40.8m",
      label: "Value of tools stolen from vehicles & properties in Victoria",
      sub: "36,708 hand & power tools, 1 year to 30 June 2025. Value up 7.5% YoY.",
      source: "RACV — How to protect tradie tools from theft",
    },
    {
      iconKey: "shield-alert",
      big: "~5%",
      label: "Estimated share of stolen tools returned to owners",
      sub: "Industry research citing Queensland Police figures.",
      source: "MapTrack industry research · QPS",
    },
  ],
  trendLabel: "Motor vehicle theft victims · Australia",
  trendBlurb:
    "Recorded motor-vehicle theft victims in Australia have climbed every year since 2021, with 2025 setting a fresh record.",
  trendSource: "Source: Australian Bureau of Statistics — Recorded Crime, 2019–2025.",
  trend: [
    { year: "2019", v: 55_000 },
    { year: "2020", v: 50_000 },
    { year: "2021", v: 50_600 },
    { year: "2022", v: 52_000 },
    { year: "2023", v: 60_200 },
    { year: "2024", v: 65_308 },
    { year: "2025", v: 66_625 },
  ],
  showStateBreakdown: true,
  sources: [
    {
      name: "Australian Bureau of Statistics",
      title: "Recorded Crime — Victims, 2025",
      url: "https://www.abs.gov.au/statistics/people/crime-and-justice/recorded-crime-victims/latest-release",
      domain: "abs.gov.au",
    },
    {
      name: "RACV",
      title: "How to protect tradie tools from theft",
      url: "https://www.racv.com.au/royalauto/moving/cars/how-to-protect-tradie-tools-from-theft.html",
      domain: "racv.com.au",
    },
    {
      name: "Queensland Police",
      title: "Tool Security guidance",
      url: "https://www.police.qld.gov.au/safety-and-preventing-crime/tool-security",
      domain: "police.qld.gov.au",
    },
  ],
  methodology:
    "All figures are the most recent published at time of writing. ABS counts victims (not individual vehicles). Recovery estimates are industry research citing police figures and are not independently verified by every category.",

  // Insurance offset calculator maths.
  currencySymbol: "$",
  excessLabel: "Your insurance excess",
  excessHint: "What you pay out-of-pocket to claim.",
  packPrice: 29,
  basePremium: 1600, // median AU comprehensive premium
  claimUplift: 0.08,
  insuranceSourceBlurb: "median AU comprehensive premium",
};

const US = {
  country: "United States",
  flagLabel: "United States · 2025",
  routeLabel: "US theft statistics",
  canonical: "/us-theft-statistics",
  seoTitle: "US Theft Statistics 2025 | Tracksy",
  seoDescription:
    "The real numbers behind America's asset theft problem — 659,880 vehicles reported stolen in 2025, trailers hitched off driveways, tools taken from job sites, and how little gets recovered. Sources: NICB, EngiMarket.",

  // Headline (short) stats used on the home TheftUrgency panel.
  headline: "659,880 vehicles stolen last year.",
  subhead: "1 in 5 are never recovered.",
  bodyCopy:
    "America's asset theft problem is quietly getting worse — cars, trailers, toolboxes, heavy equipment, bikes. Police can't be everywhere. By the time it's flagged stolen, the trail is cold. The best time to track what's yours was yesterday. The second best is right now.",
  bricks: [
    { big: "659,880", small: "Vehicles reported stolen nationwide" },
    { big: "$273.6m", small: "Trailer theft losses, 1 year" },
    { big: "~8.5%", small: "Of stolen tools recovered" },
  ],
  // The big donut: focus on the ~91.5% of stolen tools never recovered.
  donut: { percent: 91, label: "Of stolen hand & power tools are NEVER recovered" },
  donutLabelShort: "Never recovered",
  donutCopy:
    "EngiMarket's 2025 analysis of US tool theft incidents found only 8.5% included a record of recovered tools. For every 20 tools taken, more than 18 stay gone.",
  donutSource: "EngiMarket — US tool theft analysis, 2025.",
  sourcesLabel: "Sources: National Insurance Crime Bureau · EngiMarket",

  // Full /theft-statistics page content.
  heroBadge: "US theft statistics · 2025",
  heroHeadline: "A vehicle is stolen in America",
  heroHeadlineAccent: "every 48 seconds.",
  heroBody:
    "Vehicles, trailers, tools, heavy equipment, jobsite kit. America is in the middle of a quiet theft wave — and once your gear is gone, the stats say it almost never comes back. These are the real, source-backed numbers. The authorities won't find most of it. Time to protect yourself.",
  cards: [
    {
      iconKey: "car",
      big: "659,880",
      label: "Vehicles reported stolen in the US in 2025",
      sub: "~21% of stolen vehicles went unrecovered — roughly 138,000 cars, trucks and SUVs still gone.",
      source: "National Insurance Crime Bureau — 2025 report",
    },
    {
      iconKey: "hammer",
      big: "230,905",
      label: "Hand & power tool theft incidents recorded in 2025",
      sub: "Only 8.5% of incidents included any record of recovered tools. The rest walk off job sites, out of vans, and into resale channels.",
      source: "EngiMarket — US tool theft analysis, 2025",
    },
    {
      iconKey: "truck",
      big: "$273.6m",
      label: "Reported value of trailer theft in 2025",
      sub: "31,390 trailers reported stolen. Only 23.3% recovered — most never seen again.",
      source: "EngiMarket — US trailer theft analysis, 2025",
    },
  ],
  trendLabel: "Vehicles reported stolen · United States",
  trendBlurb:
    "US motor-vehicle theft hit a modern peak in 2023 at just over a million before NICB programs clawed it back. 2025 still sits far above pre-pandemic levels.",
  trendSource: "Source: National Insurance Crime Bureau — annual reports, 2019–2025.",
  trend: [
    { year: "2019", v: 721_885 },
    { year: "2020", v: 810_400 },
    { year: "2021", v: 890_600 },
    { year: "2022", v: 1_001_967 },
    { year: "2023", v: 1_020_729 },
    { year: "2024", v: 821_245 },
    { year: "2025", v: 659_880 },
  ],
  // We don't ship state-level US data — hide that section rather than fake it.
  showStateBreakdown: false,
  // Extra US-only block: equipment theft summary.
  extras: [
    {
      iconKey: "wrench",
      big: "17,403",
      label: "Heavy construction & industrial machinery theft incidents",
      sub: "~$336.3M in reported losses. Only 12.4% of incidents included recovered equipment.",
      source: "EngiMarket — US heavy equipment theft analysis, 2025",
    },
  ],
  sources: [
    {
      name: "National Insurance Crime Bureau",
      title: "Vehicle Theft Report — 2025",
      url: "https://www.nicb.org/",
      domain: "nicb.org",
    },
    {
      name: "EngiMarket",
      title: "US Theft Analysis — tools, equipment, trailers (2025)",
      url: "https://engimarket.com/",
      domain: "engimarket.com",
    },
  ],
  methodology:
    "All figures are the most recent published at time of writing. NICB counts vehicles reported stolen to law enforcement; EngiMarket's analysis is based on reported-incident records. Recovery rates reflect what's recorded as recovered, not necessarily returned intact.",

  // Insurance offset calculator maths.
  currencySymbol: "$",
  excessLabel: "Your insurance deductible",
  excessHint: "What you pay out-of-pocket to claim.",
  packPrice: 25, // USD approx per Tracksy
  basePremium: 1800, // median US comprehensive premium (annual, approx)
  claimUplift: 0.09,
  insuranceSourceBlurb: "median US comprehensive premium",
};

// US-specific scrolling incidents for NewsMarquee. State codes + locations.
export const US_INCIDENTS = [
  { state: "TX", city: "Houston",        item: "F-150 stolen from driveway overnight", when: "2 days ago" },
  { state: "CA", city: "Los Angeles",    item: "2 trailers hitched off a work yard",   when: "3 days ago" },
  { state: "FL", city: "Miami",          item: "Jet ski + trailer gone from marina",   when: "4 days ago" },
  { state: "GA", city: "Atlanta",        item: "$22k toolbox stripped from van",       when: "5 days ago" },
  { state: "AZ", city: "Phoenix",        item: "Skid steer taken from job site",       when: "5 days ago" },
  { state: "WA", city: "Seattle",        item: "Mountain bike cut from garage",        when: "6 days ago" },
  { state: "CO", city: "Denver",         item: "Tundra stolen from front yard",        when: "1 week ago" },
  { state: "IL", city: "Chicago",        item: "Cargo trailer hitched off overnight",  when: "1 week ago" },
  { state: "NY", city: "Long Island",    item: "Boat + trailer stolen at night",       when: "1 week ago" },
  { state: "NC", city: "Charlotte",      item: "Tradie van tools cleaned out",         when: "2 weeks ago" },
  { state: "OH", city: "Columbus",       item: "E-bike stolen from apartment lot",     when: "2 weeks ago" },
  { state: "NV", city: "Las Vegas",      item: "Tacoma gone from casino lot",          when: "2 weeks ago" },
];

export function theftStatsFor(isUS) {
  return isUS ? US : AU;
}
